import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import {
  ticketRespond,
  ticketRespondPanelQuery,
  ticketXdrActionsQuery,
  type TicketRespondAction,
  type TicketRespondPanel,
  type TicketRespondSession,
} from '../api/securitee'
import { Modal } from '../ui/Modal'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'
import { isRoot } from '../securitee/respondGuards'

/**
 * Respond din tichetul XDR (docs XDR 05, PR 3), in desfacerea unei detectii.
 *
 * Aceleasi actiuni ca Respond din Securitee - izolarea pe niveluri, eliberarea,
 * blocarea ecranului, log off, oprirea - plus Block IP si Disable account (cu
 * tinta propusa din evenimentul detectiei, scrisa in confirmare), pe aparatul
 * INCIDENTULUI tichetului:
 * il alege serverul, nu ecranul. Fiecare actiune pleaca din detectia deschisa,
 * cere confirmare si, pe un server, numele serverului scris. Dupa ea, banda
 * "Response actions" de sus arata pasii ei.
 */

interface Detection {
  ID: number
  title: string
}

/**
 * Ce scrie pe buton, in titlul confirmarii si pe butonul ei; `note` spune in
 * confirmare ce face actiunea si ce NU face (Lock screen nu opreste un atacator
 * care nu foloseste ecranul).
 */
export const RESPOND_ACTIONS: Record<TicketRespondAction, { button: string; title: string; confirm: string; className: string; note?: string }> = {
  isolate: { button: 'Isolate fully', title: 'Isolate the device', confirm: 'Isolate', className: 'btn btn-danger' },
  isolate_internal: { button: 'Isolate from LAN', title: 'Isolate from the local network', confirm: 'Isolate', className: 'btn btn-danger' },
  isolate_external: { button: 'Isolate from internet', title: 'Isolate from the internet', confirm: 'Isolate', className: 'btn btn-danger' },
  release: { button: 'Release isolation', title: 'Release isolation', confirm: 'Release', className: 'btn btn-primary' },
  lock: {
    button: 'Lock screen',
    title: 'Lock the screen',
    confirm: 'Lock',
    className: 'btn btn-warning',
    note: 'Disconnects every user session: whoever is at the keyboard or on remote desktop has to sign in again. Nothing is closed. It does not stop a remote attacker who does not use the screen; isolate the device for that.',
  },
  logoff: {
    button: 'Log off',
    title: 'Log off user',
    confirm: 'Log off',
    className: 'btn btn-warning',
    note: "Closes the user's programs in that session; unsaved work is lost.",
  },
  shutdown: { button: 'Shut down', title: 'Shut down device', confirm: 'Shut down device', className: 'btn btn-danger' },
  block_ip: {
    button: 'Block IP',
    title: 'Block an IP address',
    confirm: 'Block',
    className: 'btn btn-danger',
    note: 'Blocks traffic to and from this address on the device (Windows Firewall, nftables or iptables). One address or a range up to /16. The OptimumDesk server cannot be blocked.',
  },
  unblock_ip: { button: 'Unblock IP', title: 'Unblock an IP address', confirm: 'Unblock', className: 'btn btn-primary' },
  disable_account: {
    button: 'Disable account',
    title: 'Disable a local account',
    confirm: 'Disable',
    className: 'btn btn-danger',
    note: 'The account cannot sign in any more and its sessions are closed; on Linux its SSH keys are refused too. Local accounts only: disable a domain account in Active Directory.',
  },
  enable_account: { button: 'Enable account', title: 'Enable a local account', confirm: 'Enable', className: 'btn btn-primary' },
}

/** Actiunile cu tinta scrisa: adresa sau cont. */
const TARGETS: Partial<Record<TicketRespondAction, { label: string; from: 'ip' | 'user' }>> = {
  block_ip: { label: 'IP address or range', from: 'ip' },
  unblock_ip: { label: 'IP address or range', from: 'ip' },
  disable_account: { label: 'Account name', from: 'user' },
  enable_account: { label: 'Account name', from: 'user' },
}

/** Ce propune detectia: adresa sursei si contul din evenimentul ei. */
export interface RespondSuggest {
  ip?: string
  user?: string
}

const LEVELS = { internal: ' from LAN', external: ' from internet' }

interface Pending {
  action: TicketRespondAction
  session?: TicketRespondSession
}

const CONTAIN = ['block_ip', 'unblock_ip', 'disable_account', 'enable_account'] as const

/**
 * `preset`: actiunea recomandata (docs XDR 05, PR 6). Panoul se deschide direct
 * pe confirmarea ei, o data pe cerere (`presetAsk` creste la fiecare clic, deci
 * si dupa un Cancel) - daca rolul o are si incidentul e deschis.
 * Log off nu se intreaba singur: omul alege pe cine scoate.
 */
export function TicketXdrRespond({
  ticket,
  detection,
  preset = null,
  presetAsk = 0,
  suggest = {},
}: {
  ticket: number
  detection: Detection
  preset?: TicketRespondAction | null
  presetAsk?: number
  suggest?: RespondSuggest
}) {
  const client = useQueryClient()
  const query = useQuery(ticketRespondPanelQuery(ticket))
  const [pending, setPending] = useState<Pending | null>(null)
  const [typed, setTyped] = useState('')
  const [target, setTarget] = useState('')
  const [busy, setBusy] = useState(false)
  const [refusal, setRefusal] = useState('')

  const asked = useRef<string | null>(null)

  useEffect(() => {
    const panel = query.data
    const ask = `${preset}:${presetAsk}`

    if (!panel || preset === null || preset === 'logoff' || asked.current === ask) return

    asked.current = ask

    if (panel.open && allowed(panel, preset)) {
      setTarget(suggested(preset, suggest))
      setPending({ action: preset })
    }
  }, [query.data, preset, presetAsk, suggest])

  if (query.isPending) return <Spinner />

  if (query.isError) {
    return <p className="color-red">{query.error instanceof Error ? query.error.message : 'Respond could not be opened.'}</p>
  }

  const panel = query.data
  const device = panel.device.name

  // tichetul s-a rezolvat intre timp: serverul n-ar mai primi actiunea
  if (!panel.open) {
    return <p className="font-grey-cascade xdr-respond-off">The incident is closed: no more response actions. Reopen the ticket to respond.</p>
  }

  const ask = (action: TicketRespondAction, session?: TicketRespondSession) => {
    setRefusal('')
    setTyped('')
    setTarget(suggested(action, suggest))
    setPending({ action, session })
  }

  const send = async (what: Pending) => {
    setBusy(true)

    try {
      const answer = await ticketRespond(ticket, {
        action: what.action,
        detection_id: detection.ID,
        confirm: panel.device.server ? typed : undefined,
        username: what.session?.user_name,
        session_id: what.session?.id,
        target: TARGETS[what.action] ? target.trim() : undefined,
      })

      if (answer.ok) {
        toast(`${RESPOND_ACTIONS[what.action].title}: sent to ${device}.`, 'Respond', 'success')
      } else {
        setRefusal(answer.message ?? 'The action was not sent.')
      }
    } catch (e) {
      setRefusal(e instanceof Error ? e.message : 'The action was not sent.')
    } finally {
      setBusy(false)
      setPending(null)
      void client.invalidateQueries({ queryKey: ticketXdrActionsQuery(ticket).queryKey })
      void client.invalidateQueries({ queryKey: ticketRespondPanelQuery(ticket).queryKey })
    }
  }

  const spec = pending ? RESPOND_ACTIONS[pending.action] : null
  const what = pending && spec ? spec.title + (pending.session ? ` ${pending.session.user_name}` : '') : ''
  const serverOk = !panel.device.server || typed.trim().toLowerCase() === device.toLowerCase()
  const needs = pending ? TARGETS[pending.action] : undefined
  const targetOk = !needs || target.trim() !== ''

  return (
    <div className="xdr-respond-panel">
      <p className="xdr-respond-device">
        <strong>{device}</strong>
        {panel.device.server && <span className="label label-danger xdr-server">Server</span>}
        <span className="font-grey-cascade"> · {isolationText(panel.isolation)}</span>
      </p>

      <div className="xdr-respond-actions">
        {panel.can.isolate && <IsolationButtons state={panel.isolation.state} busy={busy} onAsk={ask} />}
        {panel.can.lock && (
          <button type="button" className="btn btn-sm btn-default" disabled={busy} onClick={() => ask('lock')}>
            {RESPOND_ACTIONS.lock.button}
          </button>
        )}
        {panel.can.shutdown && (
          <button type="button" className="btn btn-sm btn-default" disabled={busy} onClick={() => ask('shutdown')}>
            {RESPOND_ACTIONS.shutdown.button}
          </button>
        )}
      </div>

      {panel.can.contain && (
        <div className="xdr-respond-actions xdr-respond-contain">
          {CONTAIN.map((a) => (
            <button key={a} type="button" className="btn btn-sm btn-default" disabled={busy} onClick={() => ask(a)}>
              {RESPOND_ACTIONS[a].button}
            </button>
          ))}
        </div>
      )}

      {panel.can.logoff && panel.sessions.length > 0 && (
        <div className="xdr-respond-sessions">
          {panel.sessions.map((s) => (
            <button
              key={s.id}
              type="button"
              className="btn btn-xs btn-default"
              disabled={busy || s.logged_off || isRoot(s.user_name)}
              title={isRoot(s.user_name) ? 'root cannot be logged off; isolate or shut down the device instead' : undefined}
              onClick={() => ask('logoff', s)}
            >
              {s.logged_off ? `${s.user_name} logged off` : `Log off ${s.user_name}`}
            </button>
          ))}
        </div>
      )}

      {refusal !== '' && (
        <p className="color-red xdr-respond-error" role="alert">
          {refusal}
        </p>
      )}

      <Modal
        id="xdrRespondConfirm"
        open={pending !== null}
        onClose={() => setPending(null)}
        title={spec?.title ?? ''}
        portal
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setPending(null)}>
              Cancel
            </button>
            <button type="button" className={spec?.className ?? 'btn btn-primary'} disabled={busy || !serverOk || !targetOk} onClick={() => pending && void send(pending)}>
              {spec?.confirm ?? 'OK'}
            </button>
          </>
        }
      >
        <p>
          <strong>{what}</strong> on <strong>{device}</strong>, from detection: {detection.title}
        </p>
        {needs && (
          <div className="form-group">
            <label htmlFor="xdrRespondTarget">{needs.label}</label>
            <input id="xdrRespondTarget" className="form-control" autoComplete="off" spellCheck={false} value={target} onChange={(e) => setTarget(e.target.value)} />
          </div>
        )}
        {spec?.note && <p className="xdr-respond-note">{spec.note}</p>}
        {panel.device.server && (
          <div className="form-group">
            <label htmlFor="xdrRespondServerName">
              {device} is a server. Type its name to confirm.
            </label>
            <input id="xdrRespondServerName" className="form-control" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} />
          </div>
        )}
      </Modal>
    </div>
  )
}

/** Isolate pe cele trei niveluri cand aparatul nu e izolat; Release cand e (sau e pe drum). */
function IsolationButtons({ state, busy, onAsk }: { state: TicketRespondPanel['isolation']['state']; busy: boolean; onAsk(a: TicketRespondAction): void }) {
  if (state === 'isolated' || state === 'isolating') {
    return (
      <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => onAsk('release')}>
        {RESPOND_ACTIONS.release.button}
      </button>
    )
  }

  return (
    <>
      {(['isolate', 'isolate_internal', 'isolate_external'] as const).map((a) => (
        <button key={a} type="button" className="btn btn-sm btn-danger" disabled={busy || state === 'releasing'} onClick={() => onAsk(a)}>
          {RESPOND_ACTIONS[a].button}
        </button>
      ))}
    </>
  )
}

function isolationText(i: TicketRespondPanel['isolation']): string {
  const level = i.level === 'internal' || i.level === 'external' ? LEVELS[i.level] : ''
  const since = i.since ? ` since ${i.since}` : ''

  switch (i.state) {
    case 'isolated':
      return `Isolated${level}${since}`
    case 'isolating':
      return `Isolation${level} pending${since}`
    case 'releasing':
      return `Release pending${since}`
    default:
      return 'Not isolated'
  }
}

/** Tinta propusa din detectie pentru actiunea asta, sau gol. */
function suggested(action: TicketRespondAction, suggest: RespondSuggest): string {
  const from = TARGETS[action]?.from

  return (from === 'ip' ? suggest.ip : from === 'user' ? suggest.user : '') ?? ''
}

/** Rolul are actiunea, si (pentru Release) aparatul chiar e izolat. */
function allowed(panel: TicketRespondPanel, action: TicketRespondAction): boolean {
  switch (action) {
    case 'lock':
      return panel.can.lock
    case 'logoff':
      return panel.can.logoff
    case 'shutdown':
      return panel.can.shutdown
    case 'block_ip':
    case 'unblock_ip':
    case 'disable_account':
    case 'enable_account':
      return panel.can.contain
    default:
      return panel.can.isolate
  }
}
